import { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import BookingDialog from "@/components/BookingDialog";
import { Award, Heart, Target } from "lucide-react";

const ANDREA_IMAGE = "/images/andrea.jpg";
const INTERIOR_IMAGE = "/images/barberia.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

const values = [
  {
    icon: Heart,
    title: "Passione",
    description: "L'amore per ciò che facciamo deve arrivare al cliente, quando lo fai con sincerità, il resto viene da sé.",
  },
  {
    icon: Target,
    title: "Determinazione",
    description: "Quando credi in qualcosa, non puoi mai fermarti, nemmeno davanti agli ostacoli. Devi dare sempre il massimo.",
  },
  {
    icon: Award,
    title: "Eccellenza",
    description: "Primo premio categoria barba nel 2020. La voglia di migliorarsi e formarsi con i migliori porta grandi risultati.",
  },
];

export const ChiSiamoPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-kairos-black" data-testid="chi-siamo-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="chi-siamo-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Chi Sono
          </motion.p>
          <motion.h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            La mia storia
          </motion.h1>
        </div>
      </section>

      {/* Andrea's Story */}
      <section className="py-16 px-6" data-testid="andrea-story">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-start">
            <motion.div 
              className="relative order-2 lg:order-1"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={ANDREA_IMAGE}
                  alt="Andrea Dessì"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-kairos-black/60 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-kairos-gold p-6 md:p-8">
                <p className="text-kairos-black font-accent text-sm uppercase tracking-wider">
                  Fondatore
                </p>
                <p className="text-kairos-black font-serif text-2xl font-semibold">
                  Andrea Dessì
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-8 tracking-tight">
                Ciao a tutti, sono <span className="text-kairos-gold">Andrea Dessì</span>
              </h2>
              
              <div className="space-y-6 text-white/70 leading-relaxed">
                <p>
                  Sono il fondatore di KAIROS LA BARBERIA. Mi sono diplomato in ragioneria, 
                  ma presto mi sono reso conto che le mansioni che avevo ricoperto fino a quel 
                  momento non mi appagavano.
                </p>
                
                <p>
                  È stato a 26 anni che mi sono completamente avvicinato al mondo della barberia, 
                  un settore che già da giovane mi affascinava profondamente. Fin da ragazzo, 
                  infatti, avevo un forte legame con questo mestiere: nonostante l'appuntamento 
                  fosse nelle prime ore del mattino, passavo ore intere nella bottega del mio 
                  barbiere, osservando ogni gesto, ogni movimento.
                </p>

                <p>
                  Insoddisfatto della mia situazione, decisi di fare un bilancio della mia vita. 
                  Mi chiesi: "Cosa mi rende veramente felice e spensierato? Posso trasformare 
                  questa passione in una professione?" La risposta fu immediata: "Sì, lo posso fare!"
                </p>

                <p>
                  Così, senza esitazioni, iniziai a contattare barbieri in tutta Italia: da Palermo 
                  a Milano, passando per Roma e Napoli. La mia determinazione era talmente forte 
                  che sarei stato disposto a trasferirmi in qualsiasi parte d'Italia pur di imparare 
                  il mestiere.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journey Continuation */}
      <section className="py-16 px-6 bg-kairos-gray/20" data-testid="andrea-journey">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="space-y-6 text-white/70 leading-relaxed">
            <p>
              Il destino volle che mi rispondesse un barbiere della mia stessa regione, a "soli" 
              50 km da casa. Lui fu chiaro fin da subito: "Se in sei mesi riusciremo a costruire 
              qualcosa, ben venga; altrimenti, amici come prima." A quel punto, tutto dipendeva da me.
            </p>

            <p>
              Fu l'opportunità che stavo cercando. Iniziai con corsi di formazione interni, mattinate 
              passate a sbarbare mio padre, a sfumare i capelli di amici, parenti e conoscenti, 
              cercando di rubare con gli occhi ogni singolo movimento dai miei colleghi più esperti. 
              E dopo sei mesi, arrivò il mio primo taglio di capelli retribuito.
            </p>

            <p>
              Dopo un anno e mezzo di esperienza, decisi, insieme alla mia compagna, di trasferirmi 
              in Veneto, a Peschiera del Garda, con l'obiettivo di crescere ulteriormente. La voglia 
              di migliorarmi, di mettermi sempre in discussione e di formarmi con i migliori mi ha 
              portato a ottenere i primi, grandi risultati: <span className="text-kairos-gold font-semibold">
              nel 2020 vinsi il primo premio nella categoria barba</span> in uno degli eventi più importanti d'Italia.
            </p>

            <p>
              Dopo sei anni da collaboratore, è arrivato il momento di realizzare il mio sogno: 
              dare vita alla mia barberia, la mia dimora, il mio progetto. Così, nel 2023, ho aperto 
              KAIROS LA BARBERIA a Desenzano del Garda.
            </p>

            <blockquote className="border-l-2 border-kairos-gold pl-6 py-4 my-8">
              <p className="text-white font-serif text-xl md:text-2xl italic">
                "Caro cliente, incantati davanti al mestiere più bello e nobile del mondo."
              </p>
              <footer className="text-kairos-gold mt-4">— Andrea Dessì</footer>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
              I Miei Valori
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white tracking-tight">
              Ciò in cui credo
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center p-8"
                data-testid={`value-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 border border-kairos-gold/30 mb-6">
                  <value.icon className="w-8 h-8 text-kairos-gold" />
                </div>
                <h3 className="font-serif text-xl text-white mb-4">{value.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interior Image */}
      <section className="py-16 px-6" data-testid="interior-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative aspect-[21/9] overflow-hidden"
          >
            <img
              src={INTERIOR_IMAGE}
              alt="Kairos La Barberia Interior"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-kairos-black via-transparent to-kairos-black/30" />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-center"
              >
                <p className="text-kairos-gold font-accent text-sm uppercase tracking-[0.3em] mb-2">
                  Desenzano del Garda
                </p>
                <h3 className="font-serif text-3xl md:text-5xl text-white">
                  La mia dimora
                </h3>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-kairos-gray/20" data-testid="chi-siamo-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 tracking-tight">
              Vieni a conoscermi
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-10">
              Prenota il tuo appuntamento e vivi l'esperienza Kairos.
            </p>
            <button
              onClick={() => setBookingOpen(true)}
              className="bg-kairos-gold text-kairos-black px-12 py-5 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300"
              data-testid="chi-siamo-book-btn"
            >
              Prenota Ora
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
};

export default ChiSiamoPage;
